import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Sets browser tab title to "<Page Name> · EthicCred" based on the current route.
 * Updates on every navigation. Static map keeps it simple — falls back to
 * just "EthicCred" for unknown routes.
 */
const ROUTE_TITLES = [
  // exact matches first
  ["/",                          "Home"],
  ["/login",                     "Sign in"],
  ["/register",                  "Register"],
  ["/forgot-password",           "Reset password"],
  ["/adentry",                   "Admin entry"],
  ["/pricing",                   "Pricing"],
  ["/institutions",              "For Institutions"],
  ["/employers",                 "For Employers"],
  ["/for-employers",             "For Employers"],
  ["/sitemap",                   "Sitemap"],
  ["/developers",                "Developers"],
  ["/connected-accounts",        "Connected Accounts"],
  ["/admin/partners",            "Admin · Partners"],
  ["/get-recognized",            "Get Recognized"],
  ["/for-students",              "For Students"],
  ["/trust-safety",              "Trust & Safety"],
  ["/verify",                    "Verify"],
  ["/demo",                      "Request a Demo"],
  ["/dashboard",                 "Dashboard"],
  ["/passport",                  "My Passport"],
  ["/my-records",                "My Records"],
  ["/certificates",              "Certificates"],
  ["/org-profile",               "Organization Profile"],
  ["/billing",                   "Billing"],
  ["/notifications",             "Notifications"],
  ["/profile",                   "My Profile"],
  ["/workspace/employees",       "Employees"],
  ["/workspace/employees/add",   "Add Employee"],
  ["/workspace/employees/bulk",  "Bulk Upload"],
  ["/workspace/students",        "Students"],
  ["/workspace/students/add",    "Add Student"],
  ["/workspace/students/bulk",   "Bulk Upload"],
  ["/workspace/certificates/issue", "Issue Certificate"],
  ["/workspace/approvals",       "Approval Inbox"],
  ["/workspace/settings/approval", "Approval Settings"],
  ["/workspace/staff",           "HR Staff & Roles"],
  ["/workspace/my-awards",       "My Awards"],
  ["/admin",                     "Admin"],
  ["/admin/users",               "Admin · Users"],
  ["/admin/organizations",       "Admin · Organizations"],
  ["/admin/institutions",        "Admin · Institutions"],
  ["/admin/certificates",        "Admin · Certificates"],
  ["/admin/disputes",            "Admin · Disputes"],
  ["/admin/audit",               "Admin · Audit"],
  ["/admin/cms",                 "Admin · CMS"],
  ["/admin/staff",               "Admin · Staff"],
  ["/admin/ops",                 "Admin · Ops"],
  ["/admin/inquiries",           "Admin · Inquiries"],
  ["/admin/awards",              "Admin · Institution Awards"],
  ["/admin/mfa",                 "Admin · MFA"],
  ["/admin/trust-safety",        "Admin · Trust & Safety"],
  ["/admin/commerce",            "Admin · Commerce"],
  ["/admin/platform-config",     "Admin · Platform Config"],
];

const PREFIX_TITLES = [
  ["/workspace/employees/",      (rest) => `Employee · ${rest.slice(0, 8)}`],
  ["/verify/institution/",       (rest) => `Verify Award · ${rest}`],
  ["/institutions/",             () => "Institution"],
  ["/passport/",                 (rest) => `Passport · ${rest}`],
  ["/verify/",                   (rest) => `Verify · ${rest}`],
];

function resolveTitle(pathname) {
  for (const [p, t] of ROUTE_TITLES) {
    if (pathname === p) return t;
  }
  for (const [prefix, fn] of PREFIX_TITLES) {
    if (pathname.startsWith(prefix)) return fn(pathname.slice(prefix.length));
  }
  return "";
}

export default function usePageTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    const page = resolveTitle(pathname);
    document.title = page ? `${page} · EthicCred` : "EthicCred";
  }, [pathname]);
}
